import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import { useApolloClient } from '@apollo/client'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  useToursQuery,
  useTourFullQuery,
  useCloneTourPoiMutation,
  useCloneContentSectionMutation,
  useCloneContentNuggetMutation,
  useCloneContentByIdMutation
} from '@typings/graphql'

export type CopyLevel = 'poi' | 'section' | 'nugget' | 'content'

type Props = {
  open: boolean
  onClose: () => void
  level: CopyLevel
  /** Id of the item being copied. */
  sourceId: string
  /** Known current parent ids for pre-filling; missing ancestors are derived from the tree. */
  currentPoiId?: string
  currentSectionId?: string
  currentNuggetId?: string
  onCopied?: () => void
}

export const CopyEntityDialog: React.FC<Props> = ({
  open,
  onClose,
  level,
  sourceId,
  currentPoiId,
  currentSectionId,
  currentNuggetId,
  onCopied
}) => {
  const { t } = useTranslation()
  const { id: currentTourId, modeId: currentModeId } = useParams()

  const { data: toursData } = useToursQuery({ skip: !open })
  const tours = useMemo(() => toursData?.tours ?? [], [toursData])

  const [tourId, setTourId] = useState<string>()
  const [modeId, setModeId] = useState<string>()
  const [poiId, setPoiId] = useState<string>()
  const [sectionId, setSectionId] = useState<string>()
  const [nuggetId, setNuggetId] = useState<string>()

  const modes = useMemo(() => tours.find((tr) => tr.id === tourId)?.modes ?? [], [tours, tourId])

  const { data: treeData } = useTourFullQuery({
    variables: { id: tourId as string, modeId: modeId as string },
    skip: !open || !tourId || !modeId
  })

  const pois = useMemo(() => treeData?.tour?.mode?.pois ?? [], [treeData])
  const selectedPoi = pois.find((p) => p.id === poiId)
  const sections = useMemo(() => selectedPoi?.sections ?? [], [selectedPoi])
  const selectedSection = sections.find((s) => s.id === sectionId)
  const nuggets = useMemo(() => selectedSection?.nuggets ?? [], [selectedSection])

  // Start from the item's current tour/mode when the dialog opens.
  const derived = useRef(false)
  useEffect(() => {
    if (!open) { derived.current = false; return }
    setTourId(currentTourId)
    setModeId(currentModeId)
  }, [open])

  // Pre-fill the parent path once the current tree has loaded (initial open only).
  useEffect(() => {
    if (!open || derived.current || !pois.length) return
    if (tourId !== currentTourId || modeId !== currentModeId) return

    let p = currentPoiId
    let s = currentSectionId
    const n = currentNuggetId

    if (!p && (currentSectionId || currentNuggetId)) {
      for (const poi of pois) {
        for (const sec of poi.sections) {
          if (sec.id === currentSectionId || sec.nuggets.some((nug) => nug.id === currentNuggetId)) {
            p = poi.id
            s = sec.id
          }
        }
      }
    }

    setPoiId(p ?? pois[0]?.id)
    setSectionId(s)
    setNuggetId(n)
    derived.current = true
  }, [open, pois, tourId, modeId])

  // Auto-select the sole option when a level offers no further choice (single child).
  useEffect(() => {
    if (pois.length === 1 && !pois.some((p) => p.id === poiId)) {
      setPoiId(pois[0].id)
    }
  }, [pois])

  useEffect(() => {
    if (sections.length === 1 && !sections.some((s) => s.id === sectionId)) {
      setSectionId(sections[0].id)
    }
  }, [sections])

  useEffect(() => {
    if (nuggets.length === 1 && !nuggets.some((n) => n.id === nuggetId)) {
      setNuggetId(nuggets[0].id)
    }
  }, [nuggets])

  const client = useApolloClient()
  const [error, setError] = useState(false)

  const [clonePoi, { loading: l1 }] = useCloneTourPoiMutation()
  const [cloneSection, { loading: l2 }] = useCloneContentSectionMutation()
  const [cloneNugget, { loading: l3 }] = useCloneContentNuggetMutation()
  const [cloneContent, { loading: l4 }] = useCloneContentByIdMutation()
  const loading = l1 || l2 || l3 || l4

  const refetch = { refetchQueries: 'active' as const, awaitRefetchQueries: true }

  // Drop the target parent from the cache so the copy is visible when navigating there;
  // normalized cache-first reads would otherwise show the pre-copy list.
  const evictTarget = () => {
    const id =
      level === 'content' ? client.cache.identify({ __typename: 'ContentNugget', id: nuggetId })
        : level === 'nugget' ? client.cache.identify({ __typename: 'ContentSection', id: sectionId })
          : level === 'section' ? client.cache.identify({ __typename: 'TourPoi', id: poiId })
            : client.cache.identify({ __typename: 'TourMode', id: modeId })

    client.cache.evict({ id })
    client.cache.gc()
  }

  const canCopy =
    (level === 'poi' && !!modeId) ||
    (level === 'section' && !!poiId) ||
    (level === 'nugget' && !!sectionId) ||
    (level === 'content' && !!nuggetId)

  const handleCopy = async () => {
    setError(false)
    try {
      if (level === 'poi') {
        await clonePoi({ variables: { poiId: sourceId, tourModeId: modeId as string }, ...refetch })
      } else if (level === 'section') {
        await cloneSection({ variables: { id: sourceId, targetPoiId: poiId }, ...refetch })
      } else if (level === 'nugget') {
        await cloneNugget({ variables: { id: sourceId, targetSectionId: sectionId }, ...refetch })
      } else {
        await cloneContent({
          variables: { data: { contentId: sourceId, targetNuggetId: nuggetId as string, tourModeId: modeId as string } },
          ...refetch
        })
      }
      evictTarget()
      onCopied?.()
      onClose()
    } catch (e) {
      setError(true)
    }
  }

  const showStation = level !== 'poi'
  const showSection = level === 'nugget' || level === 'content'
  const showNugget = level === 'content'

  const dropdown = (
    label: string,
    value: string | undefined,
    options: { id: string, title: string }[],
    onChange: (v: string) => void
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? ''}
        // Only one option means no real choice: keep it selected but greyed out.
        disabled={options.length <= 1}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o, index) => (
          <MenuItem key={o.id} value={o.id}>{o.title?.trim() ? o.title : `${label} ${index + 1}`}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    // stopPropagation prevents React portal events from bubbling to the parent Card onClick
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{t('edit.copy.title')}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {dropdown(t('common.tour'), tourId, tours, (v) => {
            setTourId(v)
            setModeId(tours.find((tr) => tr.id === v)?.modes[0]?.id)
            setPoiId(undefined); setSectionId(undefined); setNuggetId(undefined)
          })}

          {dropdown(t('common.mode'), modeId, modes, (v) => {
            setModeId(v)
            setPoiId(undefined); setSectionId(undefined); setNuggetId(undefined)
          })}

          {showStation && dropdown(t('common.poi'), poiId, pois, (v) => {
            setPoiId(v); setSectionId(undefined); setNuggetId(undefined)
          })}

          {showSection && dropdown(t('common.section'), sectionId, sections, (v) => {
            setSectionId(v); setNuggetId(undefined)
          })}

          {showNugget && dropdown(t('common.nugget'), nuggetId, nuggets, setNuggetId)}

          {showStation && !!modeId && !pois.length && <Typography variant="caption" color="text.secondary">{t('edit.copy.noStations')}</Typography>}
          {showSection && !!poiId && !sections.length && <Typography variant="caption" color="text.secondary">{t('edit.copy.noSections')}</Typography>}
          {showNugget && !!sectionId && !nuggets.length && <Typography variant="caption" color="text.secondary">{t('edit.copy.noNuggets')}</Typography>}

          {error && <Alert severity="error">{t('edit.copy.error')}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={loading} disabled={!canCopy} onClick={handleCopy}>
          {t('common.copy')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
